import React, { useState } from 'react';
import { 
  FaCube, 
  FaTools, 
  FaGraduationCap, 
  FaUserFriends, 
  FaCheckCircle, 
  FaChartLine, 
  FaSearch,
  FaBookOpen
} from 'react-icons/fa';

// Mock schema listings mapped directly from table definitions
const MOCK_DATA_MATRIX = {
  skills: [
    { name: "LLM Structured Extraction", level: 92, verifiedCount: 19, trend: "+4% this week" },
    { name: "Vector Embeddings", level: 85, verifiedCount: 14, trend: "Stable" },
    { name: "Database Migrations", level: 95, verifiedCount: 38, trend: "+12% this week" },
    { name: "Schema Validation", level: 78, verifiedCount: 11, trend: "Stable" }
  ],
  domains: [
    { name: "Backend Systems", level: 94, verifiedCount: 44, trend: "+2% this week" },
    { name: "Database Architecture", level: 89, verifiedCount: 26, trend: "Stable" },
    { name: "Data Pipelines", level: 81, verifiedCount: 18, trend: "+5% this week" }
  ],
  tools: [
    { name: "Knex.js", level: 96, verifiedCount: 24, trend: "Stable" },
    { name: "PostgreSQL", level: 90, verifiedCount: 38, trend: "+8% this week" },
    { name: "Gemini API", level: 88, verifiedCount: 15, trend: "+15% this week" },
    { name: "AWS Lambda", level: 74, verifiedCount: 9, trend: "Stable" }
  ],
  soft_skills: [
    { name: "Rapid Execution", level: 98, verifiedCount: 49, trend: "Top 1%" },
    { name: "Technical Architecture", level: 91, verifiedCount: 22, trend: "Stable" }
  ],
  certificates: [
    { name: "AWS Certified Cloud Practitioner", level: 100, verifiedCount: 1, trend: "Verified 2025" }
  ]
};

// Mock Peer directory representing "Who else has this item"
const MOCK_PEERS = [
  { name: "Sarah Jenkins", handle: "sjenkins", matchingItems: ["Knex.js", "PostgreSQL"], avatar: "SJ" },
  { name: "Alex Rivera", handle: "arivera", matchingItems: ["PostgreSQL", "Data Pipelines"], avatar: "AR" },
  { name: "Michael Chen", handle: "mchen", matchingItems: ["Gemini API", "LLM Structured Extraction"], avatar: "MC" }
];

export default function CompetencyMatrix() {
  const [activeSchemaTab, setActiveSchemaTab] = useState('skills');
  const [searchTerm, setSearchTerm] = useState('');

  const currentList = MOCK_DATA_MATRIX[activeSchemaTab].filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <div style={styles.matrixLayout}>
        
        {/* Left Side: Schema Explorer Hub */}
        <div style={styles.explorerMain}>
          
          {/* Subheader Controls */}
          <div style={styles.controlsHeader}>
            <div style={styles.searchBarWrapper}>
              <FaSearch style={styles.searchIcon} />
              <input 
                type="text" 
                placeholder={`Search ${activeSchemaTab.replace('_', ' ')}...`} 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.matrixSearchInput}
              />
            </div>
          </div>

          {/* Table Schema Tab Selectors */}
          <div style={styles.tabsStrip}>
            {Object.keys(MOCK_DATA_MATRIX).map((tableKey) => {
              const label = tableKey.toUpperCase().replace('_', ' ');
              const isActive = activeSchemaTab === tableKey;
              return (
                <button
                  key={tableKey}
                  onClick={() => { setActiveSchemaTab(tableKey); setSearchTerm(''); }}
                  style={{ ...styles.schemaTabBtn, ...(isActive ? styles.activeSchemaTabBtn : {}) }}
                >
                  {tableKey === 'tools' && <FaTools style={{ marginRight: '6px' }} />}
                  {tableKey === 'certificates' && <FaGraduationCap style={{ marginRight: '6px' }} />}
                  {tableKey !== 'tools' && tableKey !== 'certificates' && <FaCube style={{ marginRight: '6px' }} />}
                  {label}
                </button>
              );
            })}
          </div>

          {/* List Data Grid */}
          <div style={styles.matrixGridList}>
            {currentList.map((item, index) => (
              <div key={index} style={styles.matrixItemCard}>
                <div style={styles.itemMetaRow}>
                  <span style={styles.itemNameText}>{item.name}</span>
                  <div style={styles.verifiedBadgeRow}>
                    <FaCheckCircle style={styles.checkIconMin} />
                    <span style={styles.verifiedCountNum}>{item.verifiedCount} Blocks Verified</span>
                  </div>
                </div>

                {/* Performance Progress Gauge Slider bar */}
                <div style={styles.progressGaugeContainer}>
                  <div style={styles.progressGaugeLabelRow}>
                    <span style={styles.gaugePercentLabel}>Competency Depth</span>
                    <span style={styles.gaugePercentVal}>{item.level}%</span>
                  </div>
                  <div style={styles.gaugeBarBackground}>
                    <div style={{ ...styles.gaugeBarFill, width: `${item.level}%` }}></div>
                  </div>
                </div>

                {/* Card Footer Metric info */}
                <div style={styles.itemCardFooter}>
                  <span style={styles.trendText}><FaChartLine /> {item.trend}</span>
                  <button style={styles.generateCourseInlineBtn}>
                    <FaBookOpen style={{ marginRight: '4px' }} /> Sprint Syllabus
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Network Collaborative Peer Directory ("Who else has this") */}
        <aside style={styles.peerSidebar}>
          <div style={styles.sidebarHeaderContainer}>
            <FaUserFriends style={styles.sidebarHeaderIcon} />
            <h3 style={styles.sidebarTitleHeading}>Peer Verification Map</h3>
          </div>
          <p style={styles.sidebarDescText}>Connected platform node members possessing active verification blocks in matching components.</p>
          
          <div style={styles.peerListContainer}>
            {MOCK_PEERS.map((peer, idx) => (
              <div key={idx} style={styles.peerListItemCard}>
                <div style={styles.peerTopRow}>
                  <div style={styles.peerMinAvatar}>{peer.avatar}</div>
                  <div style={styles.peerIdentityBlock}>
                    <span style={styles.peerRealName}>{peer.name}</span>
                    <span style={styles.peerUserHandle}>@{peer.handle}</span>
                  </div>
                </div>
                <div style={styles.sharedItemsContainer}>
                  <span style={styles.sharedLabelText}>Shared Verification Stack:</span>
                  <div style={styles.sharedPillsRow}>
                    {peer.matchingItems.map((mi, i) => (
                      <span key={i} style={styles.sharedPillTag}>{mi}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  matrixLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '24px',
    maxWidth: '1240px',
    margin: '0 auto'
  },
  explorerMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  controlsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  searchBarWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    color: '#8b949e',
    fontSize: '14px'
  },
  matrixSearchInput: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px 12px 8px 36px',
    color: '#c9d1d9',
    fontSize: '14px',
    width: '100%',
    outline: 'none'
  },
  tabsStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    borderBottom: '1px solid #30363d',
    paddingBottom: '12px'
  },
  schemaTabBtn: {
    background: 'none',
    border: '1px solid transparent',
    borderRadius: '6px',
    color: '#8b949e',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.15s'
  },
  activeSchemaTabBtn: {
    backgroundColor: '#21262d',
    borderColor: '#30363d',
    color: '#58a6ff',
    fontWeight: '600'
  },
  matrixGridList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  matrixItemCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  itemMetaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  itemNameText: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  verifiedBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(56, 139, 253, 0.1)',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid rgba(56, 139, 253, 0.15)'
  },
  checkIconMin: {
    color: '#58a6ff',
    fontSize: '12px'
  },
  verifiedCountNum: {
    fontSize: '12px',
    color: '#c9d1d9',
    fontWeight: '500'
  },
  progressGaugeContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  progressGaugeLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#8b949e'
  },
  gaugePercentLabel: {
    fontWeight: '400'
  },
  gaugePercentVal: {
    fontWeight: '600',
    color: '#c9d1d9'
  },
  gaugeBarBackground: {
    height: '6px',
    backgroundColor: '#0d1117',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  gaugeBarFill: {
    height: '100%',
    backgroundColor: '#238636',
    borderRadius: '3px'
  },
  itemCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #21262d',
    paddingTop: '12px',
    marginTop: '2px'
  },
  trendText: {
    fontSize: '12px',
    color: '#8b949e',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  generateCourseInlineBtn: {
    background: 'transparent',
    border: '1px solid #30363d',
    borderRadius: '4px',
    color: '#c9d1d9',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s'
  },
  peerSidebar: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    height: 'fit-content'
  },
  sidebarHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sidebarHeaderIcon: {
    color: '#8b949e',
    fontSize: '16px'
  },
  sidebarTitleHeading: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  sidebarDescText: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.4'
  },
  peerListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '4px'
  },
  peerListItemCard: {
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '6px',
    padding: '12px'
  },
  peerTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },
  peerMinAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#f0f6fc'
  },
  peerIdentityBlock: {
    display: 'flex',
    flexDirection: 'column'
  },
  peerRealName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#c9d1d9'
  },
  peerUserHandle: {
    fontSize: '11px',
    color: '#8b949e'
  },
  sharedItemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    borderTop: '1px solid #21262d',
    paddingTop: '8px'
  },
  sharedLabelText: {
    fontSize: '11px',
    color: '#8b949e'
  },
  sharedPillsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px'
  },
  sharedPillTag: {
    fontSize: '11px',
    color: '#58a6ff',
    backgroundColor: 'rgba(56, 139, 253, 0.05)',
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid rgba(56, 139, 253, 0.1)'
  }
};